import React, { useEffect, useMemo, useRef, useState } from "react"
import isPermissionEnabled from "common/isPermissionEnabled"
import Breadcrumbs from "components/Common/Breadcrumb"
import { Card, CardBody, Col, Container, Row, Table } from "reactstrap"
import Filters from "components/Common/Filters"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import DownloadCSV from "components/Shared/DownloadCSV"
import DownloadXLSX from "components/Shared/DownloadXLSX"
import TableShimmer from "components/Shared/TableShimmer"
import { pdfDownload } from "helpers/api_helper"
import { EXPORT_DATA } from "helpers/url_helper"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import Paginations from "components/Common/Paginations"
import { getAllBranchs } from "store/actions/usersAction"
import { getCountryList } from "store/actions/countriesAction"
import { getAllRegionList } from "store/actions/regionAction"
import { getAllCityList } from "store/actions/cityAction"
import { getLoyaltyPointsReports } from "store/actions/reportsAction"
import { groupByForLoyaltyPoints } from "components/Common/constants"
import { getAllCustomersList } from "store/actions/customersAction"
import ReactApexChart from "react-apexcharts"
import { useTranslation } from "react-i18next"
import { getBranchCountryList } from "store/actions/commonAction"

const formatDateTime = date =>
  date
    .toLocaleString("en-CA", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })
    .replace(",", "")

const getDefaultDateRange = () => {
  const today = new Date()
  const startOfDay = new Date(today)
  startOfDay.setHours(0, 0, 0, 0)

  const endOfDay = new Date(today)
  endOfDay.setHours(23, 59, 0, 0)

  return {
    min: formatDateTime(startOfDay),
    max: formatDateTime(endOfDay),
  }
}

const LoyaltyPointsReports = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Loyalty Points Reports ")

  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1
  const [isApiCalled, setIsApiCalled] = useState(false)
  const [customerOptions, setCustomerOptions] = useState([])

  const {
    loading,
    isLoading,
    totalCount,
    totalPages,
    currentPage,
    itemsPerPage,
    loyaltyPointsReports,
  } = useSelector(state => state.Reports)

  const { branchsList, countryList } = useSelector(state => state.Common)
  const { customersDropdownList } = useSelector(state => state.Customers)
  const { userRole, accountType } = useSelector(state => state.Login)
  const colors = ["#FF4560", "#00E396", "#FEB019", "#008FFB", "#775DD0"]

  useEffect(() => {
    const canView = isPermissionEnabled(
      userRole,
      16,
      "view_loyalty_points_reports",
      accountType
    )
    if (!canView) {
      navigate("/dashboard")
    }
  }, [userRole, accountType, navigate])

  // Set default groupBy to 'customer' option from groupByForLoyaltyPoints
  const defaultGroupBy =
    groupByForLoyaltyPoints.find(item => item.value === "customer") || ""

  const [selectedFilters, setSelectedFilters] = useState({
    branchId: [],
    groupBy: defaultGroupBy,
    countryId: [],
    regionId: [],
    cityId: [],
    customerId: [],
  })
  const [showGraph, setShowGraph] = useState(false)
  const [isShowGraphBtn, setIsShowGraphBtn] = useState(true)

  const [series, setSeries] = useState([])
  const [options, setOptions] = useState({
    labels: [],
    colors: colors,
    legend: { show: !1 },
    plotOptions: {
      pie: {
        donut: {
          size: "70%",
        },
      },
    },
  })

  const [dateRange, setDateRange] = useState(() => getDefaultDateRange())
  const { regionList } = useSelector(state => state.Region)
  const { cityList } = useSelector(state => state.City)
  const flatpickrRef = useRef(null)

  useEffect(() => {
    fetchLoyaltyPointsReports()
    fetchBranchCountryList()
  }, [])

  const fetchLoyaltyPointsReports = () => {
    const hasDateRange = dateRange?.min && dateRange?.max
    const payload = {
      perPage: itemsPerPage,
      pageNo: pageNumber,
      branchId: [],
      cityId: [],
      regionId: [],
      countryId: [],
      customerId: [],
      groupBy: defaultGroupBy.value || "",
      dateRange: hasDateRange
        ? { min: dateRange.min, max: dateRange.max }
        : {},
    }
    dispatch(getLoyaltyPointsReports(payload))
    setIsApiCalled(true)
  }

  useEffect(() => {
    if (loyaltyPointsReports?.length > 0) {
      const report = loyaltyPointsReports[0]
      const { loyaltyPointsIssued, ...otherData } = report

      const filteredEntries = Object.entries(otherData)
      const filteredValues = filteredEntries.map(([_, value]) => value)
      const updatedLabel = filteredEntries.map(([key]) =>
        t(
          key
            .split(/(?=[A-Z])/)
            .pop()
            .charAt(0)
            .toUpperCase() +
            key
              .split(/(?=[A-Z])/)
              .pop()
              .slice(1)
        )
      )

      setSeries(filteredValues)
      setOptions(prevOptions => ({
        ...prevOptions,
        labels: updatedLabel,
        plotOptions: {
          pie: {
            donut: {
              labels: {
                show: true,
                total: {
                  show: true,
                  label: t("Total"),
                  formatter: () => loyaltyPointsIssued,
                },
              },
            },
          },
        },
      }))
    }
  }, [loyaltyPointsReports, language])

  const fetchBranchCountryList = () => {
    dispatch(getBranchCountryList())
  }

  const fetchRegionList = countryId => {
    dispatch(getAllRegionList(countryId))
  }

  const fetchCityList = (countryId, regionId) => {
    const payload = {
      countryId,
      regionId,
    }
    dispatch(getAllCityList(payload))
  }

  const fetchCustomersList = keyword => {
    const payload = {
      keyword,
    }
    dispatch(getAllCustomersList(payload))
  }

  const onInputChange = value => {
    value && fetchCustomersList(value)
  }

  useEffect(() => {
    if (customersDropdownList.length > 0) {
      const updatedCustomerOption = customersDropdownList?.map(item => ({
        label: `${item.firstName} ${item.lastName}, ${
          item?.phoneNumber ? item?.phoneNumber : ""
        }`,
        value: item._id,
      }))
      setCustomerOptions(updatedCustomerOption)
    }
  }, [customersDropdownList])

  const filters = useMemo(() => {
    const baseFilters = [
      {
        name: "groupBy",
        type: "singleSelect",
        options: groupByForLoyaltyPoints.map(item => ({
          label: t(item.label),
          value: item.value,
        })),
        placeholder: t("Select Group By"),
      },
      {
        name: "branchId",
        type: "multiSelect",
        options: branchsList.map(branch => ({
          label: language === "sa" ? branch.nameAr : branch.name,
          value: branch._id,
        })),
        placeholder: t("Select Branch"),
      },
      {
        name: "countryId",
        type: "singleSelect",
        options: countryList.map(item => ({
          label: language === "sa" ? item.nameAr : item.name,
          value: item._id,
        })),
        placeholder: t("Select Country"),
      },
      {
        name: "customerId",
        type: "multiSelect",
        options: customerOptions.map(item => ({
          label: item.label,
          value: item.value,
        })),
        placeholder: t("Type To Search Customer"),
      },
    ]
    let filteredFilters = baseFilters
    if (selectedFilters?.groupBy?.value === "customer") {
      filteredFilters = baseFilters.filter(f => f.name !== "branchId")
    }
    // ADD REGION DROPDOWN
    if (Object.keys(selectedFilters.countryId).length > 0) {
      filteredFilters.push({
        name: "regionId",
        type: "singleSelect",
        options: regionList.map(item => ({
          label: language === "sa" ? item.nameAr : item.name,
          value: item._id,
        })),
        placeholder: t("Select Region"),
      })
    }

    // ADD CITY DROPDOWN
    if (Object.keys(selectedFilters.regionId).length > 0) {
      filteredFilters.push({
        name: "cityId",
        type: "singleSelect",
        options: cityList.map(item => ({
          label: language === "sa" ? item.nameAr : item.name,
          value: item._id,
        })),
        placeholder: t("Select City"),
      })
    }

    return filteredFilters
  }, [
    selectedFilters?.groupBy?.value,
    selectedFilters.countryId, // Add this dependency
    selectedFilters.regionId, // Add this dependency
    branchsList,
    groupByForLoyaltyPoints,
    countryList,
    regionList, // Add this dependency
    cityList, // Add this dependency
    customerOptions,
    language,
    t,
  ])
  // useEffect(() => {
  //   if (isApiCalled) {
  //     handleSearch()
  //   }
  // }, [selectedFilters])

  // useEffect(() => {
  //   if (dateRange.min && dateRange.max) {
  //     handleSearch()
  //   }
  // }, [dateRange])

  const handleFilterChange = (selectedValue, filterName) => {
    const value = selectedValue.value
    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      [filterName]: selectedValue,
    }))
    if (filterName === "countryId") {
      // GET REGION LIST
      fetchRegionList(value)
      setSelectedFilters(prev => ({
        ...prev,
        regionId: [],
        cityId: [],
      }))
    } else if (filterName === "regionId") {
      // GET CITIES LIST
      const { countryId } = selectedFilters
      fetchCityList(countryId.value, value)
      setSelectedFilters(prev => ({
        ...prev,
        cityId: [],
      }))
    }
  }

  const getColumns = () => {
    const { groupBy } = selectedFilters
    switch (groupBy.value) {
      case "" || undefined:
        return [
          "Loyalty Points Issued",
          "Loyalty Points Redeemed",
          "Loyalty Points Revoked",
          "Loyalty Points Expired",
          "Loyalty Points Available",
          "Available Amount",
        ]
      case "order":
        return [
          "Invoice No",
          "Loyalty Points Issued",
          "Loyalty Points Redeemed",
          "Available Amount",
        ]
      case "branch":
        return [
          "Branch Name",
          "Branch Ref",
          "Loyalty Points Issued",
          "Loyalty Points Redeemed",
          "Loyalty Points Expired",
          "Available Amount",
        ]
      case "city":
        return [
          "City Name",
          "Loyalty Points Issued",
          "Loyalty Points Redeemed",
          "Loyalty Points Expired",
          "Available Amount",
        ]
      case "region":
        return [
          "Region Name",
          "Loyalty Points Issued",
          "Loyalty Points Redeemed",
          "Loyalty Points Expired",
          "Available Amount",
        ]
      case "country":
        return [
          "Country Name",
          "Loyalty Points Issued",
          "Loyalty Points Redeemed",
          "Loyalty Points Expired",
          "Available Amount",
        ]
      case "customer":
        return [
          "Customer Name",
          // "Customer Email",
          "Customer PhoneNumber",
          "Opening Balance",
          "Loyalty Points Issued",
          "Loyalty Points Redeemed",
          "Loyalty Points Expired",
          "Loyalty Points Revoked",
          "Loyalty Points Available",
          "Available Amount",
          "Closing Balance",
        ]
    }
  }

  const columns = getColumns()

  const renderCell = (item, column) => {
    switch (column) {
      case "Loyalty Points Issued":
        return item.loyaltyPointsIssued !== undefined
          ? formatNumber(item.loyaltyPointsIssued)
          : "-"
      case "Loyalty Points Redeemed":
        return item.loyaltyPointsRedeemed !== undefined
          ? formatNumber(item.loyaltyPointsRedeemed)
          : "-"
      case "Loyalty Points Revoked":
        return item.loyaltyPointsRevoked !== undefined
          ? formatNumber(item.loyaltyPointsRevoked)
          : "-"
      case "Loyalty Points Expired":
        return item.loyaltyPointsExpired !== undefined
          ? formatNumber(item.loyaltyPointsExpired)
          : "-"
      case "Loyalty Points Available":
        return item.loyaltyPointsAvailable === undefined
          ? "-"
          : item.loyaltyPointsAvailable < 0
          ? 0
          : formatNumber(item.loyaltyPointsAvailable)
      case "Available Amount":
        return item.availableAmount === undefined
          ? "-"
          : item.availableAmount < 0
          ? 0
          : formatNumber(item.availableAmount)
      case "Invoice No":
        return item.invoiceNo
      case "Branch Name":
        return language === "sa" ? item.branchNameAr : item.branchName
      case "Branch Ref":
        return item.branchRef
      case "City Name":
        return language === "sa" ? item.cityNameAr : item.cityName
      case "Region Name":
        return language === "sa" ? item.regionNameAr : item.regionName
      case "Country Name":
        return language === "sa" ? item.countryNameAr : item.countryName
      case "Customer Name":
        return item.customerName || "-"
      case "Customer Email":
        return item.customerEmail || "-"
      case "Customer PhoneNumber":
        return item.customerPhoneNumber ? (
          <span dir="ltr" style={{ unicodeBidi: "bidi-override" }}>
            {item.customerPhoneNumber}
          </span>
        ) : (
          "-"
        )
      case "Opening Balance":
        return item.openingBalance !== undefined
          ? formatNumber(item.openingBalance)
          : "-"
      case "Closing Balance":
        return item.closingBalance !== undefined
          ? formatNumber(item.closingBalance)
          : "-"
      default:
        return item[column] || ""
    }
  }

  function formatNumber(number) {
    return Number(number)?.toFixed(2)
  }

  const handleSearch = () => {
    const { branchId, groupBy, countryId, regionId, cityId, customerId } =
      selectedFilters
    const branchIds = branchId?.map(item => item.value) || []
    const customerIds = customerId?.map(item => item.value) || []
    const groupByValue = groupBy.value || ""
    const countryValue = countryId.value ? [countryId.value] : []
    const regionValue = regionId.value ? [regionId.value] : []
    const cityValue = cityId.value ? [cityId.value] : []

    // HIDE GRAPH WHENEVER APPLY FILTER
    setShowGraph(false)

    if (groupBy === "" || groupBy.value === "") {
      setIsShowGraphBtn(true)
    } else {
      setIsShowGraphBtn(false)
      setShowGraph(false)
    }

    const hasDateRange = dateRange?.min && dateRange?.max
    const payload = {
      perPage: itemsPerPage,
      pageNo: 1,
      branchId: branchIds,
      cityId: cityValue,
      regionId: regionValue,
      countryId: countryValue,
      customerId: customerIds,
      groupBy: groupByValue,
      dateRange: hasDateRange
        ? { min: dateRange.min, max: dateRange.max }
        : {},
    }

    dispatch(getLoyaltyPointsReports(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`/loyalty-points-reports?page=${1}`)
        }
      })
      .catch(error => {})
  }

  const handleReset = () => {
    const defaultDateRange = getDefaultDateRange()
    const payload = {
      perPage: itemsPerPage,
      pageNo: pageNumber,
      branchId: [],
      cityId: [],
      regionId: [],
      countryId: [],
      customerId: [],
      groupBy: defaultGroupBy.value || "",
      dateRange: defaultDateRange,
    }
    dispatch(getLoyaltyPointsReports(payload))
    navigate(`/loyalty-points-reports?page=${1}`)
    setSelectedFilters({
      branchId: [],
      groupBy: defaultGroupBy,
      cityId: [],
      regionId: [],
      countryId: [],
      customerId: [],
    })
    setDateRange(defaultDateRange)
    if (flatpickrRef.current) {
      flatpickrRef.current.flatpickr.setDate(
        [defaultDateRange.min, defaultDateRange.max],
        true
      )
    }
  }

  const onPageChange = page => {
    const { branchId, groupBy, countryId, regionId, cityId, customerId } =
      selectedFilters
    const branchIds = branchId?.map(item => item.value) || []
    const customerIds = customerId?.map(item => item.value) || []
    const groupByValue = groupBy.value || ""
    const countryValue = countryId.value ? [countryId.value] : []
    const regionValue = regionId.value ? [regionId.value] : []
    const cityValue = cityId.value ? [cityId.value] : []

    const hasDateRange = dateRange?.min && dateRange?.max
    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      branchId: branchIds,
      cityId: cityValue,
      regionId: regionValue,
      countryId: countryValue,
      customerId: customerIds,
      groupBy: groupByValue,
      dateRange: hasDateRange
        ? { min: dateRange.min, max: dateRange.max }
        : {},
    }

    dispatch(getLoyaltyPointsReports(payload))
    navigate(`/loyalty-points-reports?page=${page}`)
  }

  const formatLabel = key => {
    return key
      .split(/(?=[A-Z])/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  }

  const formatValue = value => {
    return typeof value === "number"
      ? `${value.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`
      : value
  }

  const getColor = index => {
    return colors[index % colors.length]
  }

  const exportToCSV = async () => {
    const { branchId, groupBy, countryId, regionId, cityId, customerId } =
      selectedFilters
    const branchIds = branchId?.map(item => item.value) || []
    const customerIds = customerId?.map(item => item.value) || []
    const groupByValue = groupBy.value || ""
    const countryValue = countryId.value ? [countryId.value] : []
    const regionValue = regionId.value ? [regionId.value] : []
    const cityValue = cityId.value ? [cityId.value] : []

    const hasDateRange = dateRange?.min && dateRange?.max
    const payload = {
      type: "REPORT-LOYALTY-POINTS",
      exportType: "csv",
      branchId: branchIds,
      regionId: regionValue,
      cityId: cityValue,
      countryId: countryValue,
      customerId: customerIds,
      groupBy: groupByValue,
      dateRange: hasDateRange
        ? { min: dateRange.min, max: dateRange.max }
        : {},
    }

    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const csvData = response
      const fileName = hasDateRange
        ? t(`loyalty_points_report_from_${dateRange.min}_to_${dateRange.max}`)
        : t("Loyalty Points Report Till Date")
      DownloadCSV(csvData, fileName)
    }
  }

  const handleExcelDownload = async () => {
    const { branchId, groupBy, countryId, regionId, cityId, customerId } =
      selectedFilters
    const branchIds = branchId?.map(item => item.value) || []
    const customerIds = customerId?.map(item => item.value) || []
    const groupByValue = groupBy.value || ""
    const countryValue = countryId.value ? [countryId.value] : []
    const regionValue = regionId.value ? [regionId.value] : []
    const cityValue = cityId.value ? [cityId.value] : []

    const hasDateRange = dateRange?.min && dateRange?.max
    const payload = {
      type: "REPORT-LOYALTY-POINTS",
      exportType: "xlsx",
      branchId: branchIds,
      regionId: regionValue,
      cityId: cityValue,
      countryId: countryValue,
      customerId: customerIds,
      groupBy: groupByValue,
      dateRange: hasDateRange
        ? { min: dateRange.min, max: dateRange.max }
        : {},
    }
    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const xlsxData = response
      const fileName = hasDateRange
        ? t(`loyalty_points_report_from_${dateRange.min}_to_${dateRange.max}`)
        : t("Loyalty Points Report Till Date")
      DownloadXLSX(xlsxData, fileName)
    }
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs />
        <Row>
          {showGraph && (
            <Col xl={12}>
              <Card>
                <CardBody>
                  <h4 className="card-title mb-4">
                    {t("Loyalty Points Chart")}
                  </h4>
                  <Row>
                    <Col lg={6}>
                      <div>
                        <div id="donut-chart">
                          <ReactApexChart
                            options={options}
                            series={series}
                            type="donut"
                            height={260}
                            className="apex-charts"
                          />
                        </div>
                      </div>
                    </Col>
                    <Col lg={6}>
                      <div className="row text-muted">
                        {loyaltyPointsReports[0] &&
                          Object.entries(loyaltyPointsReports[0])
                            ?.filter(([key]) => key !== "loyaltyPointsIssued")
                            ?.map(([key, value], index) => (
                              <div className="col-6 mt-4" key={key}>
                                <p className="mb-2 text-truncate">
                                  <i
                                    className={`mdi mdi-circle  me-1`}
                                    style={{ color: getColor(index) }}
                                  />{" "}
                                  {t(formatLabel(key))}
                                </p>
                                <h5>{formatValue(value)}</h5>
                              </div>
                            ))}
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          )}
          <Col xl={12}>
            <Card>
              <DataExportDropdown
                cardTitle={t("Loyalty Points Reports")}
                isAddBtn={loading ? false : isShowGraphBtn}
                addBtnText={`${showGraph ? t("Hide") : t("Show")} ${t(
                  "Graph"
                )}`}
                onAddBtnClick={() => setShowGraph(!showGraph)}
                onDownloadCSV={exportToCSV}
                onDownloadExcel={handleExcelDownload}
                isExportData={loading ? false : true}
              />
              <CardBody>
                <Filters
                  dateRangeWithTime={true}
                  filters={filters}
                  hideSearch={true}
                  handleFilterChange={handleFilterChange}
                  handleSearch={handleSearch}
                  onReset={handleReset}
                  selectedFilters={selectedFilters}
                  isRangeDate={true}
                  dateRange={dateRange}
                  flatpickrRef={flatpickrRef}
                  onInputChange={onInputChange}
                  setDateRange={setDateRange}
                  colSize={4}
                />
                <div className="table-responsive">
                  {loading ? (
                    <TableShimmer row={7} col={5} />
                  ) : loyaltyPointsReports.length === 0 ? (
                    <p className="text-center my-5 py-3">
                      {t("No loyalty points report found")}
                    </p>
                  ) : (
                    <Table className="table mb-0" hover>
                      <thead className="table-light full_th">
                        <tr>
                          {columns?.map((column, index) => (
                            <th key={index}>{t(column)}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {loyaltyPointsReports?.map((item, index) => (
                          <tr key={index}>
                            {columns?.map((column, colIndex) => (
                              <td key={colIndex}>
                                {renderCell(item, column) ?? "-"}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </div>

                {!loading && loyaltyPointsReports.length > 0 && (
                  <div className="mt-3">
                    <Paginations
                      data={loyaltyPointsReports}
                      itemsPerPage={itemsPerPage}
                      onPageChange={onPageChange}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCount={totalCount}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}
export default LoyaltyPointsReports
